import * as React from "react";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { cn } from "@/lib/utils";
import { Check, Plus } from "lucide-react";

type MultiToggleFieldProps = Omit<
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root>,
  "type" | "onChange" | "defaultValue"
> & {
  options: string[];
  value: string[];
  onChange: (values: string[]) => void;
  maxSelected?: number;
  allowOther?: boolean;
  otherPlaceholder?: string;
};

const MultiToggleField = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  MultiToggleFieldProps
>(
  (
    {
      className,
      options,
      value,
      onChange,
      maxSelected,
      allowOther = false,
      otherPlaceholder = "Other",
      ...props
    },
    ref,
  ) => {
    const [editingOtherIndex, setEditingOtherIndex] = React.useState<
      number | null
    >(null);
    const optionsSet = React.useMemo(() => new Set(options), [options]);

    const selectedFromOptions = value.filter(
      (val, idx) => optionsSet.has(val) && idx !== editingOtherIndex,
    );

    const otherItems = value.reduce<{ label: string; valueIndex: number }[]>(
      (acc, val, idx) => {
        if (!optionsSet.has(val) || idx === editingOtherIndex) {
          acc.push({ label: val, valueIndex: idx });
        }

        return acc;
      },
      [],
    );

    const reachedLimit = !!maxSelected && value.length >= maxSelected;

    const handlePresetChange = (update: string[]) => {
      if (reachedLimit && update.length > value.length) {
        return;
      }

      onChange([...update, ...otherItems.map((o) => o.label)]);
      setEditingOtherIndex(null);
    };

    const handleOtherChange = (valueIndex: number, text: string) => {
      const update = [...value];
      update[valueIndex] = text;
      onChange(update);
    };

    const handleAddOther = () => {
      if (reachedLimit) {
        return;
      }

      setEditingOtherIndex(value.length);
      onChange([...value, ""]);
    };

    const handleOtherDelete = (valueIndex: number) => {
      const update = value.filter((_, idx) => idx !== valueIndex);
      onChange(update);
      setEditingOtherIndex(null);
    };

    return (
      <>
        <ToggleGroupPrimitive.Root
          {...props}
          ref={ref}
          className={cn("flex flex-row gap-1 flex-wrap", className)}
          type="multiple"
          value={selectedFromOptions}
          onValueChange={handlePresetChange}
        >
          {options.map((option) => {
            const isSelected = selectedFromOptions.includes(option);

            return (
              <ToggleGroupItem
                key={option}
                value={option}
                label={option}
                disabled={reachedLimit && !isSelected}
                isSelected={isSelected}
              />
            );
          })}
        </ToggleGroupPrimitive.Root>
        {allowOther && (
          <div className="mt-2 flex flex-wrap gap-2">
            {otherItems.map((other) => (
              <div
                key={other.valueIndex}
                className="inline-flex items-center gap-1 rounded-full border border-brand-primary-400 bg-white px-3 py-1 text-sm"
              >
                <input
                  className="min-w-[80px] border-none bg-transparent text-sm text-brand-primary-400 outline-none"
                  placeholder={otherPlaceholder}
                  value={other.label}
                  onChange={(e) =>
                    handleOtherChange(other.valueIndex, e.target.value)
                  }
                  onFocus={() => setEditingOtherIndex(other.valueIndex)}
                  onBlur={() =>
                    setEditingOtherIndex((prev) =>
                      prev === other.valueIndex ? null : prev,
                    )
                  }
                />
                <button
                  type="button"
                  onClick={() => handleOtherDelete(other.valueIndex)}
                  className="ml-1 text-xs text-slate-400 hover:text-slate-700"
                >
                  ✕
                </button>
              </div>
            ))}

            {!reachedLimit && (
              <button
                type="button"
                onClick={handleAddOther}
                className="inline-flex items-center rounded-full border border-brand-primary-400 text-brand-primary-400 px-3 py-1 text-sm hover:bg-brand-primary-50"
              >
                + {otherItems.length === 0 ? "Other" : "Add another"}
              </button>
            )}
          </div>
        )}
      </>
    );
  },
);
MultiToggleField.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> & {
    isSelected: boolean;
    label: string;
  }
>(({ className, children, isSelected, label, ...props }, ref) => {
  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-4 py-1",
        "text-sm font-medium outline-none transition",
        "border-brand-primary-400 text-brand-primary-400 bg-white",
        "hover:bg-brand-primary-50 disabled:opacity-40 disabled:cursor-not-allowed",
        isSelected && "bg-brand-primary-50",
        className,
      )}
      {...props}
    >
      {children ?? (
        <>
          {isSelected ? <Check size={14} /> : <Plus size={14} />}
          <span className="whitespace-nowrap">{label}</span>
        </>
      )}
    </ToggleGroupPrimitive.Item>
  );
});
ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { MultiToggleField };
